import Component from "@glimmer/component";
import type { TOC } from "@ember/component/template-only";
import { get } from "@ember/helper";
import styles from "./styles.module.css";
import mergeClasses from "glesys-controlpanel/modifiers/merge-classes";
import { modifier } from "ember-modifier";
import { isNone } from "@ember/utils";
import IconsChevronDown from "glesys-controlpanel/components/icons/chevron-down";
import { and, not } from "ember-truth-helpers";

export const OptgroupLegend: TOC<{
  Blocks: { default: [] };
  Element: HTMLLegendElement;
}> = <template>
  <legend class="px-2.5 py-2.5 text-gray-500 text-xs" {{mergeClasses}} ...attributes>
    {{yield}}
  </legend>
</template>;

export const Optgroup: TOC<{
  Blocks: { default: [] };
  Element: HTMLOptGroupElement;
}> = <template>
  <optgroup ...attributes>
    {{yield}}
  </optgroup>
</template>;

export const Option: TOC<{
  Blocks: { default: [] };
  Element: HTMLOptionElement;
}> = <template>
  <option
    class="px-2.5 py-2.5 pr-8 whitespace-nowrap text-sm text-gray-800 hover:text-black flex items-center bg-white hover:bg-gray-100 checked:bg-gray-200 checked:text-black disabled:cursor-not-allowed disabled:opacity-50 enabled:cursor-pointer"
    {{mergeClasses}}
    ...attributes
  >
    {{yield}}
  </option>
</template>;

type Option = {
  value: string;
  label?: string;
  disabled?: boolean;
  title?: string;
};

type OptionGroup = {
  label: string;
  options: Option[];
};

interface SelectSignature {
  Args: {
    items?: (Option | OptionGroup)[];
    value?: string | string[];
    placeholder?: string;
    chevron?: boolean;
  };
  Blocks: {
    selectedcontent: [];
    default: [];
  };
  Element: HTMLSelectElement | HTMLDivElement;
}

export default class Select extends Component<SelectSignature> {
  get supportsCustomizableSelect() {
    return CSS.supports("appearance: base-select");
  }

  get chevron() {
    return this.args.chevron ?? true;
  }

  /*
   * When the selected value is set from outside the component, the selectedcontent won't render correctly,
   * even though the correct value is selected in the select element.
   * By re-setting the value it will render correctly,
   * but that also requires us to use an argument @value instead of the native value attribute so Ember knows when to rerender.
   */
  fixRenderOfSelectedValue = modifier((element: HTMLSelectElement) => {
    // Simple trick to track items to ensure this reruns when items change.
    this.args.items?.map(() => {});

    // multiple select seems to work
    if (element.multiple) {
      return;
    }
    const selected =
      this.args.value ?? ([...element.options].find((option) => option.selected) ?? element.options[0])?.value;
    if (!isNone(selected)) {
      element.value = selected as string;
    }
  });

  isOptionGroup(item: Option | OptionGroup): item is OptionGroup {
    return "options" in item;
  }

  isSelected(item: string, value?: string | string[]): boolean {
    return Array.isArray(value) ? value.includes(item) : item === value;
  }

  <template>
    <div
      class="relative has-[select:disabled]:opacity-50
        {{if
          (and (not this.supportsCustomizableSelect) (has-block 'selectedcontent'))
          'focus-within:outline-2 focus-within:outline-solid focus-within:outline-blue-600 focus-within:outline-offset-0'
        }}"
    >
      {{#if (and (not this.supportsCustomizableSelect) (has-block "selectedcontent"))}}
        <div
          class="{{if this.chevron (get styles 'chevron')}}
            absolute inset-0 flex items-center pointer-events-none p-3 rounded-sm border text-black border-gray-200 group"
          data-test-select-selected-content-not-supporting-customizable-select
          {{mergeClasses}}
          ...attributes
        >
          {{yield to="selectedcontent"}}
        </div>
      {{/if}}
      <select
        class="{{get styles 'select'}}
          {{if this.chevron (get styles 'chevron')}}
          {{if (and (not this.supportsCustomizableSelect) (has-block 'selectedcontent')) 'opacity-0'}}
          p-3 w-full rounded-sm border text-sm text-black border-gray-200 group disabled:cursor-not-allowed"
        data-test-select
        {{this.fixRenderOfSelectedValue}}
        {{mergeClasses}}
        ...attributes
      >
        {{! eslint-disable-next-line ember/template-no-nested-interactive }}
        <button
          {{! A button inside a select is the customizable-select pattern: the <select> owns focus and }}
          {{! keyboard interaction, and this button is just the shadow chrome the browser renders for the }}
          {{! closed state. axe-core doesn't model this yet, so we mark the button as non-focusable }}
          {{! (tabindex="-1") and hidden from the a11y tree (aria-hidden) to match the real semantics. }}
          class="relative w-full flex justify-between"
          aria-hidden="true"
          type="button"
          tabindex="-1"
        >
          <div class="pr-8" data-test-select-selected-content>
            {{#if (has-block "selectedcontent")}}
              {{yield to="selectedcontent"}}
            {{else}}
              <selectedcontent />
            {{/if}}
          </div>
          {{#if this.chevron}}
            <IconsChevronDown
              class="absolute right-0 h-full group-open:rotate-180 transition origin-center"
              data-test-select-chevron
            />
          {{/if}}
        </button>
        {{#if @placeholder}}
          <Option data-test-select-placeholder value="" disabled selected={{not @value}}>
            <span class="text-gray-600">{{@placeholder}}</span>
          </Option>
        {{/if}}
        {{#if (has-block)}}
          {{yield}}
        {{else}}
          {{#each @items as |item|}}
            {{#if (this.isOptionGroup item)}}
              <Optgroup label={{item.label}}>
                <OptgroupLegend>{{item.label}}</OptgroupLegend>
                {{#each item.options as |item|}}
                  <Option
                    value={{item.value}}
                    disabled={{item.disabled}}
                    selected={{this.isSelected item.value @value}}
                    title={{item.title}}
                  >
                    {{if item.label item.label item.value}}
                  </Option>
                {{/each}}
              </Optgroup>
            {{else}}
              <Option
                value={{item.value}}
                disabled={{item.disabled}}
                selected={{this.isSelected item.value @value}}
                title={{item.title}}
              >
                {{if item.label item.label item.value}}
              </Option>
            {{/if}}
          {{/each}}
        {{/if}}
      </select>
    </div>
  </template>
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    Select: typeof Select;
  }
}
